import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { SplashScreen } from '@capacitor/splash-screen';
import {
  BiometricService,
  BiometricEnrollmentService,
  ProjectsService,
  PwaService,
  SendIntentService,
} from '@front-end/core/services';
import {
  AlertController,
  MenuController,
  ModalController,
} from '@ionic/angular';
import { CardService, CostCenterSummary } from '@libs/core/api';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { NgxAuthService } from 'ngx-auth-utils';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  from,
  map,
  switchMap,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';
import { environment } from '../environments/environment';
import { NotificationService } from './quotes/notification.service';
import { Badge } from '@capawesome/capacitor-badge';
import { CardPinEditModalComponent } from './card-mgmt/components/card-pin-edit-modal/card-pin-edit-modal.component';

@UntilDestroy()
@Component({
  selector: 'front-end-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, AfterViewInit {
  readonly role$ = this.projectService.selectedProject$.pipe(
    map((project) => project?.role)
  );

  readonly superOperatore$ = this.projectService.selectedProject$.pipe(
    map((project) => project?.subtasks?.some((m) => m.superOperatore))
  );

  readonly selectedProject$ = this.projectService.selectedProject$;

  readonly isBuyerInSelectedProject$ =
    this.projectService.isBuyerInSelectedProject();

  readonly isSuperBuyerInSelectedProject$ =
    this.projectService.isSuperBuyerInSelectedProject();

  readonly canMakePurchase$ = combineLatest([
    this.isBuyerInSelectedProject$,
    this.isSuperBuyerInSelectedProject$,
  ]).pipe(
    map(([isBuyer, isSuperBuyer]) => {
      return isBuyer || isSuperBuyer;
    })
  );

  selectedCompany$ = this.projectService.selectedProject$.pipe(
    map((project) => project?.companyId),
    filter((companyId) => !!companyId)
  );

  showCostCentersList = false;

  constructor(
    private readonly authenticationService: NgxAuthService,
    private readonly menuController: MenuController,
    private readonly router: Router,
    private readonly projectService: ProjectsService,
    private readonly translateService: TranslateService,
    private readonly modalCtrl: ModalController,
    private readonly updatePwaService: PwaService,
    private readonly sendIntentService: SendIntentService,
    private readonly notificationService: NotificationService,
    private readonly cardService: CardService,
    private readonly modalController: ModalController,
    private readonly alertController: AlertController,
    private readonly biometricEnrollmentService: BiometricEnrollmentService,
    private readonly biometricService: BiometricService
  ) {
    window.onpopstate = async () => {
      const modal = await this.modalCtrl.getTop();

      if (modal) {
        this.modalCtrl.dismiss();
        history.go(1);
        return;
      }
    };
  }

  readonly appVersion = environment.appVersion;

  currentYear = new Date().getFullYear();

  ngOnInit(): void {
    this.authenticationService.initialize().subscribe();

    this.updatePwaService
      .checkForUpdates()
      .pipe(
        debounceTime(1000),
        switchMap(() => this.updatePwaService.update()),
        untilDestroyed(this)
      )
      .subscribe();

    this.authenticationService.events.subscribe((event) => {
      if (event.type === 'login' || event.type === 'auto-login') {
        this.notificationService.setupPushNotifications();
      }
    });

    this.translateService.use('it');
    this.checkIntent();

    Badge.clear().then(() => {});

    this.selectedCompany$
      .pipe(
        withLatestFrom(this.canMakePurchase$, this.selectedProject$),
        distinctUntilChanged(
          ([company1, canPurchase1], [company2, canPurchase2]) =>
            company1 === company2 && canPurchase1 === canPurchase2
        ),
        filter(([, canMakePurchase, project]) => canMakePurchase && !!project),
        switchMap(([, , project]) =>
          this.cardService.getCardByProjectId({
            projectId: project.id as number,
          })
        ),
        tap((card) => {
          if (!card.active) {
            this.openActivateCardAlert();
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  ngAfterViewInit() {
    timer(2000).subscribe(() => SplashScreen.hide());
  }

  @HostListener('window:sendIntentReceived')
  onSendIntentReceived() {
    this.checkIntent();
  }

  checkIntent() {
    this.sendIntentService
      .checkSendIntentReceived()
      .pipe(
        first(),
        catchError(() => EMPTY)
      )
      .subscribe((intent) => {
        if (intent && intent.url) {
          this.sendIntentService.addIntent(intent);
        }
      });
  }

  logout() {
    this.closeMenu();
    this.notificationService.removePushNotificationToken();
    // Reset biometric auth before standard auth to allow proper cleaning of secrets
    this.biometricEnrollmentService.resetEnrollment();
    this.authenticationService.logout();
    this.router.navigate(['/user', 'login']);
  }

  closeMenu() {
    this.menuController.close();
  }

  selectProject($event: CostCenterSummary) {
    this.projectService.selectProject($event);
    this.showCostCentersList = false;
    this.closeMenu();
    this.router.navigate(['/']);
  }

  toggleCostCentersList(): void {
    this.showCostCentersList = true;
  }

  openActivateCardAlert() {
    from(
      this.alertController.create({
        header: this.translateService.instant('pages.card-mgmt.alert.title'),
        message: this.translateService.instant('pages.card-mgmt.alert.message'),
        buttons: [
          {
            text: this.translateService.instant('pages.card-mgmt.alert.notNow'),
            role: 'cancel',
          },
          {
            text: this.translateService.instant(
              'pages.card-mgmt.alert.activate'
            ),
            role: 'activate',
          },
        ],
      })
    )
      .pipe(
        switchMap((alert) => from(alert.present()).pipe(map(() => alert))),
        switchMap((alert) => from(alert.onWillDismiss())),
        tap(({ role }) => {
          if (role === 'activate') {
            this.openActivateCardModal();
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  openActivateCardModal(): void {
    from(
      this.modalController.create({
        component: CardPinEditModalComponent,
        componentProps: {
          isActivation: true,
        },
      })
    )
      .pipe(
        tap((modal) => modal.present()),
        switchMap((modal) => from(modal.onWillDismiss())),
        untilDestroyed(this)
      )
      .subscribe();
  }
}
